// Упоминания элементов библиотеки в промпте: в хранимом тексте — ссылка по id
// (`@[element:<uuid>]`), в поле ввода и в истории — имя. Формат плейсхолдера
// общий с бэкендом (`app/modules/ai/domain/element_mentions.py`), провайдеру он
// не уходит никогда: имя подставляется в момент отправки запроса.
//
// Зачем так: имя, зашитое в текст, ломается при переименовании элемента —
// старое `@Кет` повисает в промпте мусором, а реальное упоминание уезжает в
// конец фразы. Ссылка по id переживает переименование, а показываем всегда
// актуальное имя.

/** Элемент, на который можно сослаться в промпте. */
export interface MentionableElement {
    id: string;
    name: string;
}

const MENTION_RE =
    /@\[element:([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})\]/g;
const REPEATED_SPACES_RE = /[^\S\n]{2,}/g;

/** Плейсхолдер упоминания — то, что уходит в хранимый текст промпта. */
export function buildElementMention(elementId: string): string {
    return `@[element:${elementId}]`;
}

function escapeForRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/gu, "\\$&");
}

/**
 * Имена в порядке «сначала длинные»: иначе элемент `Кот` съел бы начало
 * упоминания `@Котёнок` и связь ушла бы не на тот элемент.
 */
function byNameLengthDesc(a: MentionableElement, b: MentionableElement): number {
    return b.name.length - a.name.length;
}

/** Текст поля ввода → хранимый промпт: `@Имя` заменяется ссылкой по id. */
export function toStoredPrompt(text: string, elements: readonly MentionableElement[]): string {
    let stored = text;
    for (const element of [...elements].sort(byNameLengthDesc)) {
        if (element.name.length === 0) continue;
        stored = stored.replace(
            new RegExp(`@${escapeForRegExp(element.name)}`, "giu"),
            buildElementMention(element.id),
        );
    }
    return stored;
}

/**
 * Хранимый промпт → текст для показа: ссылка по id разворачивается в актуальное
 * имя. Элемент, которого больше нет в выборе (удалён из библиотеки, убран из
 * запроса), вырезается вместе со ссылкой — оставлять `@[element:…]` на глазах у
 * пользователя нельзя, а имени для него уже нет.
 */
export function toDisplayPrompt(stored: string, elements: readonly MentionableElement[]): string {
    const nameById = new Map(elements.map((element) => [element.id.toLowerCase(), element.name]));
    const substituted = stored.replace(MENTION_RE, (_match, id: string) => {
        const name = nameById.get(id.toLowerCase());
        return name === undefined ? "" : `@${name}`;
    });
    return substituted.replace(REPEATED_SPACES_RE, " ").trim();
}

/**
 * Элементы, чьё имя встречается в промпте обычным словом («Стол» при тексте
 * «запрыгивает на стол»).
 *
 * Kling требует, чтобы имя элемента не пересекалось с текстом промпта, иначе
 * связывание `@` разъезжается. Бэкенд такой конфликт разрешает сам — отправит
 * элемент под именем `СтолA`, — поэтому это предупреждение, а не ошибка:
 * отправку оно не блокирует, но объясняет, почему стоит переименовать элемент.
 * Само упоминание (`@Стол`) конфликтом не считается — это ссылка на элемент.
 */
export function findPromptNameCollisions(
    text: string,
    elements: readonly MentionableElement[],
): MentionableElement[] {
    const haystack = toDisplayPrompt(text, elements).toLowerCase();
    return elements.filter((element) => {
        if (element.name.length === 0) return false;
        return new RegExp(`(?<!@)${escapeForRegExp(element.name.toLowerCase())}`, "u").test(haystack);
    });
}
